import React, { useEffect, useState } from "react";
import useSWR from "swr";
import Overview, { fetcher, optionsFetcher } from "../../components/Overview/Overview";
import { getCompetenceProfiles } from "../../queries/competence_profiles";
import Select from "react-select-new";
import cx from "classnames";
import moment from "moment";
import Dialog from "rc-dialog";
import { EmployerSpecificTooltip } from "../courses/components/tooltips";
// import Quickview from "../../components/Quickview";
// import { CompetenceProfileOverviewDefinitionBase } from "./CompetenceProfileOverview";

export const CompetenceProfileSelectorListFilter = React.memo((props) => {
    // Top level vars (Course items)
    const [competenceProfileUUID, setCompetenceProfileUUID] = useState(props?.competenceProfileDetails?.uuid);

    const [confirmSwitch, setConfirmSwitch] = useState(false);

    // Filtering related
    const [typeFilter, setTypeFilter] = useState("");
    const [kindFilter, setKindFilter] = useState("");
    const [languageFilter, setLanguageFilter] = useState("");
    const [eLearningFilter, setELearningFilter] = useState(null);
    const [employerSpecificFilter, setEmployerSpecificFilter] = useState(null);
    const [trainerSpecificFilter, setTrainerSpecificFilter] = useState(null);
    const [cpNameFilter, setCpNameFilter] = useState("");

    const [disableTrainerSpecific, setDisableTrainerSpecific] = useState(false);

    const [stats, setStats] = useState({
        type: [],
        repetition: [],
        language: [],
        contains_elearning: [],
        allow_employer_specific: [],
        trainers: []
    });

    const emptySelection = [
        typeFilter === "",
        kindFilter === "",
        languageFilter === "",
        eLearningFilter === null,
        employerSpecificFilter === null,
        trainerSpecificFilter === null,
        cpNameFilter === ""
    ].every((o) => o === true);

    const [courseEvents, setCourseEvents] = useState(false);
    const [scheduledEvents, setScheduledEvents] = useState(false);

    // filter object for SWR
    const [filter, setFilter] = useState({});

    // Options used in the competence profile filtering
    const languageOptions = [
        {
            value: "nl",
            display_name: "Nederlands"
        },
        {
            value: "en",
            display_name: "Engels"
        }
    ];

    // Options used in the competence profile filtering
    const elearningOptions = [
        {
            value: true,
            display_name: "E-learning"
        },
        {
            value: false,
            display_name: "Geen E-learning"
        }
    ];

    const employerSpecificOptions = [
        {
            value: true,
            display_name: "Bedrijfsspecifiek"
        },
        {
            value: false,
            display_name: "Niet Bedrijfsspecifiek"
        }
    ];

    const trainerSpecificOptions = [
        {
            value: true,
            display_name: "Opleiderspecifiek"
        }
    ];

    const { data: typeOptions } = useSWR(() => "/v1/competenceprofiletypes/", fetcher);
    const kind_options = [
        {
            value: "base",
            bool: false,
            display_name: "Basis"
        },
        {
            value: "repetition",
            bool: true,
            display_name: "Herhaling"
        }
    ];

    const now = moment();

    const minMaxDateText = (minDate, maxDate, containsElearning) => {
        const min = minDate ? moment(minDate, "YYYY-MM-DD") : false;
        const max = maxDate ? moment(maxDate, "YYYY-MM-DD") : false;

        if (min && max) {
            return `Beschikbaar tussen ${min.format("D MMM", ["nl"])} en ${max.format("D MMM", ["nl"])}`;
        }
        if (min && !max) {
            const date = containsElearning ? moment(min).add(28, "days") : min;
            if (date > moment() && containsElearning) {
                return `Beschikbaar vanaf ${date.format("D MMM", ["nl"])}`;
            }
        }
        if (!min && max) {
            return `Beschikbaar tot ${max.format("D MMM", ["nl"])}`;
        }
    };

    const competenceProfilesSWR = getCompetenceProfiles(filter, false);
    const competenceProfiles = competenceProfilesSWR.data; // Filtered competence profiles

    useEffect(() => {
        if (props?.competenceProfileDetails?.uuid && props.competenceProfileDetails.uuid !== competenceProfileUUID) {
            setCompetenceProfileUUID(props.competenceProfileDetails.uuid);
        }
    }, [props?.competenceProfileDetails?.uuid]);

    useEffect(() => {
        if (competenceProfileUUID && competenceProfileUUID !== props?.competenceProfileDetails?.uuid) {
            if (props?.setReturnValue && typeof props?.setReturnValue === "function") {
                const cp = competenceProfiles?.find((o) => o.uuid === competenceProfileUUID);
                if (cp) {
                    props.setReturnValue(cp);
                }
            }
        }
    }, [competenceProfileUUID, competenceProfiles, props?.competenceProfileDetails?.uuid]);

    useEffect(() => {
        setFilter({
            filter: {
                is_active: 1,
                type: typeFilter ? typeFilter.slug : "",
                repetition: kindFilter?.value === "repetition" ? true : kindFilter?.value === "base" ? false : "",
                language: languageFilter && languageOptions?.length > 0 && languageOptions.find((o) => o.value === languageFilter.value) ? languageFilter.value : "",
                search: cpNameFilter,
                contains_elearning: eLearningFilter ? eLearningFilter.value : null,
                allow_employer_specific: employerSpecificFilter ? employerSpecificFilter.value : null,
                trainers: trainerSpecificFilter ? props.trainer.uuid : ""
            }
        });
    }, [eLearningFilter, employerSpecificFilter, typeFilter, kindFilter, languageFilter, trainerSpecificFilter, cpNameFilter, props.trainer]);

    useEffect(() => {
        // If uuid not in current selection of CP's, we should unselect the selected UUID
    }, [competenceProfileUUID, competenceProfiles]);

    useEffect(() => {
        if (props?.courseEvents) {
            setCourseEvents(props.courseEvents);
        }
        if (props?.scheduledEvents) {
            setScheduledEvents(props.scheduledEvents);
        }
    }, [props?.courseEvents, props?.scheduledEvents]);

    useEffect(() => {
        if (!(!competenceProfilesSWR.error && !competenceProfilesSWR?.data)) {
            const stats = {
                type: [],
                repetition: [],
                language: [],
                contains_elearning: [],
                allow_employer_specific: [],
                trainers: []
            };

            // determine filters to potentially disable`
            competenceProfiles?.forEach((competenceProfile) => {
                Object.keys(stats).forEach((key) => {
                    const val = competenceProfile[key];
                    if (val !== undefined && val !== null && !Array.isArray(val)) {
                        if (!stats[key].includes(competenceProfile[key])) {
                            stats[key].push(competenceProfile[key]);
                        }
                    }
                    if (Array.isArray(val)) {
                        if (val?.length === 0) {
                            stats[key] = [...stats[key], ""];
                        }
                        stats[key] = [...stats[key], ...val];
                    }
                });
            });
            stats.trainers = [...new Set(stats.trainers)];
            setStats(stats);
        }
    }, [competenceProfiles, competenceProfilesSWR]);

    useEffect(() => {
        if (disableTrainerSpecific === false && emptySelection && stats.trainers.length > 0 && stats.trainers.filter((o) => o !== "") < 1) {
            // there appear to be no trainer options for an empty filtering selection so we can disable "trainer specific"
            setDisableTrainerSpecific(true);
        }
    }, [disableTrainerSpecific, emptySelection, stats]);

    return (
        <>
            {confirmSwitch && (
                <Dialog
                    title={"Competentieprofiel wijzigen"}
                    onClose={() => setConfirmSwitch(false)}
                    visible={true}
                    animation="fade"
                    footer={[
                        <button
                            type="button"
                            className="btn btn-light"
                            key="no"
                            onClick={() => {
                                setConfirmSwitch(false);
                            }}
                        >
                            Nee, competentieprofiel niet wijzigen
                        </button>,
                        <button
                            type="button"
                            className="btn btn-primary"
                            key="yes"
                            onClick={() => {
                                setConfirmSwitch(false);
                                setCompetenceProfileUUID(confirmSwitch);
                            }}
                        >
                            Ja, wijzig het competentieprofiel
                        </button>
                    ]}
                >
                    <p className={"paragraph"}>
                        Door het wijzigen van het competentieprofiel zullen alle reeds ingevulde gegevens worden verwijderd.
                        <br />
                        <br />
                        Weet je zeker dat je het competentieprofiel wilt wijzigen?
                    </p>
                </Dialog>
            )}
            <h2 className="form-section__title">Selecteer het type cursus</h2>
            <div className="form-section__wrapper">
                <div className="form-section__row row">
                    <div className="form-input col-4">
                        <label className="form-input__label">Type</label>

                        {stats.type.length <= 1 && !typeFilter ? (
                            <input
                                className={"form-control form-control-md form-control--disabled"}
                                disabled
                                type={"text"}
                                value={typeOptions?.results.find((o) => o.slug === stats?.type?.[0])?.name || "Geen optie beschikbaar"}
                            />
                        ) : (
                            <Select
                                name="type"
                                value={typeFilter}
                                className="react-select-new"
                                classNamePrefix="react-select"
                                onChange={(type) => {
                                    setTypeFilter(type);
                                }}
                                controlShouldRenderValue={true}
                                options={typeOptions?.results}
                                getOptionLabel={(type) => type.name}
                                getOptionValue={(type) => type.slug}
                                isClearable={true}
                                isSearchable={false}
                                backspaceRemovesValue={true}
                                placeholder={typeOptions?.results?.length > 0 ? "Geen voorkeur" : "Types worden geladen"}
                                noOptionsMessage={() => "Geen resultaten"}
                            />
                        )}
                    </div>
                    <div className="form-input col-4">
                        <label className="form-input__label">Soort</label>
                        {kind_options && stats?.repetition?.length <= 1 && !kindFilter ? (
                            <input
                                className={"form-control form-control-md form-control--disabled"}
                                disabled
                                type={"text"}
                                value={kind_options?.find((o) => o.bool === stats?.repetition?.[0])?.display_name || "Geen optie beschikbaar"}
                            />
                        ) : (
                            <Select
                                name="kind"
                                value={kindFilter}
                                className="react-select-new"
                                classNamePrefix="react-select"
                                onChange={(kind) => {
                                    setKindFilter(kind);
                                }}
                                controlShouldRenderValue={true}
                                options={kind_options}
                                getOptionLabel={(kind) => kind.display_name}
                                getOptionValue={(kind) => kind.value}
                                isClearable={true}
                                isSearchable={false}
                                backspaceRemovesValue={true}
                                placeholder={kind_options?.length > 0 ? "Geen voorkeur" : "Soorten worden geladen"}
                                noOptionsMessage={() => "Geen resultaten"}
                            />
                        )}
                    </div>
                    <div className="form-input col-4">
                        <label className="form-input__label">Taal</label>
                        {languageOptions && stats?.language?.length <= 1 && !languageFilter ? (
                            <input
                                className={"form-control form-control-md form-control--disabled"}
                                disabled
                                type={"text"}
                                value={languageOptions?.find((o) => o.value === stats?.language?.[0])?.display_name || "Geen optie beschikbaar"}
                            />
                        ) : (
                            <Select
                                name="language"
                                value={languageFilter}
                                className="react-select-new"
                                classNamePrefix="react-select"
                                onChange={(language) => {
                                    setLanguageFilter(language);
                                }}
                                controlShouldRenderValue={true}
                                options={languageOptions}
                                getOptionLabel={(language) => language.display_name}
                                getOptionValue={(language) => language.value}
                                isClearable={true}
                                isSearchable={false}
                                backspaceRemovesValue={true}
                                placeholder={languageOptions?.length > 0 ? "Geen voorkeur" : "Talen worden geladen"}
                                noOptionsMessage={() => "Geen resultaten"}
                            />
                        )}
                    </div>
                </div>

                <div className="form-section__row row">
                    {/*    <div className="form-input col-4">*/}
                    {/*        <label className="form-input__label">E-learning</label>*/}
                    {/*        /!*{stats.contains_elearning.length <= 1  && !eLearningFilter ? (*!/*/}
                    {/*        /!*    <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={ stats.contains_elearning.includes(true) ? "E-learning" : stats.contains_elearning.includes(false) ? "Geen e-learning" : "Geen optie beschikbaar"}/>*!/*/}
                    {/*        /!*) : (*!/*/}
                    {/*        /!*    <div className={"checkbox-deluxe"}>*!/*/}
                    {/*        /!*        <input className={"checkbox-deluxe__input"} type={"checkbox"} id={"contains_elearning"} onChange={() => setELearningFilter(!eLearningFilter)} defaultChecked={eLearningFilter}/>*!/*/}
                    {/*        /!*        <label className={"checkbox-deluxe__label"} htmlFor={"contains_elearning"}>*!/*/}
                    {/*        /!*            Filteren*!/*/}
                    {/*        /!*            <span className={"checkbox-deluxe__button"}>{eLearningFilter ? "Aan" : "Uit"}</span>*!/*/}
                    {/*        /!*        </label>*!/*/}
                    {/*        /!*    </div>*!/*/}
                    {/*        /!*)}*!/*/}
                    {/*    </div>*/}

                    <div className="col-4">
                        <label className="form-input__label">E-learning</label>
                        {stats.contains_elearning.length <= 1 && !eLearningFilter ? (
                            <>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"elearning-none"}
                                        name={"elearning"}
                                        value={""}
                                        onChange={(e) => {
                                            setELearningFilter(null);
                                        }}
                                        checked={!eLearningFilter}
                                        className={"radio__input"}
                                        disabled={true}
                                    />
                                    <label htmlFor={"elearning-none"} className={"radio__label radio__label--disabled"}>
                                        Geen voorkeur
                                    </label>
                                </div>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"elearning-true"}
                                        name={"elearning"}
                                        value={"true"}
                                        onChange={(e) => {
                                            setELearningFilter(elearningOptions[0]);
                                        }}
                                        checked={stats.contains_elearning?.includes(true)}
                                        className={"radio__input"}
                                        disabled={true}
                                    />
                                    <label htmlFor={"elearning-true"} className={"radio__label radio__label--disabled"}>
                                        E-learning
                                    </label>
                                </div>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"elearning-false"}
                                        name={"elearning"}
                                        value={"false"}
                                        onChange={(e) => {
                                            setELearningFilter(elearningOptions[1]);
                                        }}
                                        checked={stats.contains_elearning?.includes(false)}
                                        className={"radio__input"}
                                        disabled={true}
                                    />
                                    <label htmlFor={"elearning-false"} className={"radio__label radio__label--disabled"}>
                                        Geen e-learning
                                    </label>
                                </div>
                                <span className={"radio__disabled-message"}>Keuze niet mogelijk op basis van eerdere selecties</span>
                            </>
                        ) : (
                            <>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"elearning-none"}
                                        name={"elearning"}
                                        value={""}
                                        onChange={(e) => {
                                            setELearningFilter(null);
                                        }}
                                        checked={!eLearningFilter}
                                        className={"radio__input"}
                                    />
                                    <label htmlFor={"elearning-none"} className={"radio__label"}>
                                        Geen voorkeur
                                    </label>
                                </div>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"elearning-true"}
                                        name={"elearning"}
                                        value={"true"}
                                        onChange={(e) => {
                                            setELearningFilter(elearningOptions[0]);
                                        }}
                                        checked={eLearningFilter?.value === true}
                                        className={"radio__input"}
                                    />
                                    <label htmlFor={"elearning-true"} className={"radio__label"}>
                                        E-learning
                                    </label>
                                </div>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"elearning-false"}
                                        name={"elearning"}
                                        value={"false"}
                                        onChange={(e) => {
                                            setELearningFilter(elearningOptions[1]);
                                        }}
                                        checked={eLearningFilter?.value === false}
                                        className={"radio__input"}
                                    />
                                    <label htmlFor={"elearning-false"} className={"radio__label"}>
                                        Geen e-learning
                                    </label>
                                </div>
                            </>
                        )}

                        {/*{stats.contains_elearning.length <= 1  && !eLearningFilter ? (*/}
                        {/*    <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={ stats.contains_elearning.includes(true) ? "E-learning" : stats.contains_elearning.includes(false) ? "Geen e-learning" : "Geen optie beschikbaar"}/>*/}
                        {/*) : (*/}
                        {/*    <Select*/}
                        {/*        name="elearning"*/}
                        {/*        value={eLearningFilter}*/}
                        {/*        className="react-select-new"*/}
                        {/*        classNamePrefix="react-select"*/}
                        {/*        onChange={elearning => {setELearningFilter(elearning); }}*/}
                        {/*        controlShouldRenderValue={true}*/}
                        {/*        options={elearningOptions}*/}
                        {/*        getOptionLabel={elearning => elearning.display_name}*/}
                        {/*        getOptionValue={elearning => elearning.value}*/}
                        {/*        isClearable={true}*/}
                        {/*        isSearchable={false}*/}
                        {/*        backspaceRemovesValue={true}*/}
                        {/*        placeholder={elearningOptions?.length > 0 ? "Geen voorkeur" : "Opties worden geladen"}*/}
                        {/*        noOptionsMessage={() => "Geen resultaten"}*/}
                        {/*    />*/}
                        {/*)}*/}
                    </div>

                    {/*<div className="form-input col-4">*/}
                    {/*    <label className="form-input__label">Bedrijfsspecifiek</label>*/}
                    {/*    {stats.allow_employer_specific.length <= 1  && !employerSpecificFilter ? (*/}
                    {/*        <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={ stats.allow_employer_specific.includes(true) ? "Bedrijfsspecifiek" : stats.allow_employer_specific.includes(false) ? "Niet bedrijfsspecifiek" : "Geen optie beschikbaar"}/>*/}
                    {/*    ) : (*/}
                    {/*        <div className={"checkbox-deluxe"}>*/}
                    {/*            <input className={"checkbox-deluxe__input"} type={"checkbox"} id={"employer_specific"} onChange={() => setEmployerSpecificFilter(!employerSpecificFilter)} defaultChecked={employerSpecificFilter}/>*/}
                    {/*            <label className={"checkbox-deluxe__label"} htmlFor={"employer_specific"}>*/}
                    {/*                Filteren*/}
                    {/*                <span className={"checkbox-deluxe__button"}>{employerSpecificFilter ? "Aan" : "Uit"}</span>*/}
                    {/*            </label>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*</div>*/}

                    <div className="col-4">
                        <label className="form-input__label">
                            Bedrijfsspecifiek
                            <EmployerSpecificTooltip containerClass={"generic-tooltip--expressive"} identifier={"employer-specific-cp-selector"} />
                        </label>
                        {stats.allow_employer_specific.length <= 1 && !employerSpecificFilter ? (
                            <>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"employer_specific-none"}
                                        name={"employer_specific"}
                                        value={""}
                                        onChange={(e) => {
                                            setEmployerSpecificFilter(null);
                                        }}
                                        checked={!employerSpecificFilter}
                                        className={"radio__input"}
                                        disabled={true}
                                    />
                                    <label htmlFor={"employer_specific-none"} className={"radio__label radio__label--disabled"}>
                                        Geen voorkeur
                                    </label>
                                </div>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"employer_specific-true"}
                                        name={"employer_specific"}
                                        value={"true"}
                                        onChange={(e) => {
                                            setEmployerSpecificFilter(employerSpecificOptions[0]);
                                        }}
                                        checked={stats.allow_employer_specific.includes(true)}
                                        className={"radio__input"}
                                        disabled={true}
                                    />
                                    <label htmlFor={"employer_specific-true"} className={"radio__label radio__label--disabled"}>
                                        Bedrijfsspecifiek
                                    </label>
                                </div>
                                {/*<div className={"radio"}>*/}
                                {/*    <input type={"radio"} id={"employer_specific-false"} name={"employer_specific"} value={"false"}*/}
                                {/*           onChange={e => {setEmployerSpecificFilter(employerSpecificOptions[1]); }}*/}
                                {/*           checked={stats.allow_employer_specific.includes(false)}*/}
                                {/*           className={"radio__input"}*/}
                                {/*           disabled={true}*/}
                                {/*    />*/}
                                {/*    <label htmlFor={"employer_specific-false"} className={"radio__label radio__label--disabled"}>Niet bedrijfsspecifiek</label>*/}
                                {/*</div>*/}
                                <span className={"radio__disabled-message"}>Keuze niet mogelijk op basis van eerdere selecties</span>
                            </>
                        ) : (
                            <>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"employer_specific-none"}
                                        name={"employer_specific"}
                                        value={""}
                                        onChange={(e) => {
                                            setEmployerSpecificFilter(null);
                                        }}
                                        checked={!employerSpecificFilter}
                                        className={"radio__input"}
                                    />
                                    <label htmlFor={"employer_specific-none"} className={"radio__label"}>
                                        Geen voorkeur
                                    </label>
                                </div>
                                <div className={"radio"}>
                                    <input
                                        type={"radio"}
                                        id={"employer_specific-true"}
                                        name={"employer_specific"}
                                        value={"true"}
                                        onChange={(e) => {
                                            setEmployerSpecificFilter(employerSpecificOptions[0]);
                                        }}
                                        checked={employerSpecificFilter?.value === true}
                                        className={"radio__input"}
                                    />
                                    <label htmlFor={"employer_specific-true"} className={"radio__label"}>
                                        Bedrijfsspecifiek
                                    </label>
                                </div>
                                {/*<div className={"radio"}>*/}
                                {/*    <input type={"radio"} id={"employer_specific-false"} name={"employer_specific"} value={"false"}*/}
                                {/*           onChange={e => {setEmployerSpecificFilter(employerSpecificOptions[1]); }}*/}
                                {/*           checked={employerSpecificFilter?.value === false}*/}
                                {/*           className={"radio__input"}*/}
                                {/*    />*/}
                                {/*    <label htmlFor={"employer_specific-false"} className={"radio__label"}>Niet bedrijfsspecifiek</label>*/}
                                {/*</div>*/}
                            </>
                        )}
                    </div>

                    {/*<div className="col-4">*/}
                    {/*    <label className="form-input__label">Bedrijfsspecifiek<EmployerSpecificTooltip /></label>*/}
                    {/*    {stats.allow_employer_specific.length <= 1  && !employerSpecificFilter ? (*/}
                    {/*        <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={ stats.allow_employer_specific.includes(true) ? "Bedrijfsspecifiek" : stats.allow_employer_specific.includes(false) ? "Niet bedrijfsspecifiek" : "Geen optie beschikbaar"}/>*/}
                    {/*    ) : (*/}
                    {/*        <Select*/}
                    {/*            name="employer_specific"*/}
                    {/*            value={employerSpecificFilter}*/}
                    {/*            className="react-select-new"*/}
                    {/*            classNamePrefix="react-select"*/}
                    {/*            onChange={employerSpecificOption => {setEmployerSpecificFilter(employerSpecificOption); }}*/}
                    {/*            controlShouldRenderValue={true}*/}
                    {/*            options={employerSpecificOptions}*/}
                    {/*            getOptionLabel={employerSpecificOption => employerSpecificOption.display_name}*/}
                    {/*            getOptionValue={employerSpecificOption => employerSpecificOption.value}*/}
                    {/*            isClearable={true}*/}
                    {/*            isSearchable={false}*/}
                    {/*            backspaceRemovesValue={true}*/}
                    {/*            placeholder={elearningOptions?.length > 0 ? "Geen voorkeur" : "Opties worden geladen"}*/}
                    {/*            noOptionsMessage={() => "Geen resultaten"}*/}
                    {/*        />*/}
                    {/*    )}*/}
                    {/*</div>*/}

                    {/*<div className="form-input col-4">*/}
                    {/*    <label className="form-input__label">Opleider specifiek</label>*/}
                    {/*    {(stats.trainers.length <= 1 || (!stats.trainers.includes(props.trainer.uuid) && !stats.trainers.includes(""))) && !trainerSpecificFilter ? (*/}
                    {/*        <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={ stats.trainers.includes(props.trainer.uuid) ? "Opleiderspecifiek" : !stats.trainers.includes(props.trainer.uuid) ? "Niet opleider specifiek" : "Geen optie beschikbaar"}/>*/}
                    {/*    ) : (*/}
                    {/*        <div className={"checkbox-deluxe"}>*/}
                    {/*            <input className={"checkbox-deluxe__input"} type={"checkbox"} id={"trainer_specific"} onChange={() => setTrainerSpecificFilter(!trainerSpecificFilter)} defaultChecked={trainerSpecificFilter}/>*/}
                    {/*            <label className={"checkbox-deluxe__label"} htmlFor={"trainer_specific"}>*/}
                    {/*                Filteren*/}
                    {/*                <span className={"checkbox-deluxe__button"}>{trainerSpecificFilter ? "Aan" : "Uit"}</span>*/}
                    {/*            </label>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*</div>*/}

                    {disableTrainerSpecific === false && (
                        <div className="col-4">
                            <label className="form-input__label">Opleiderspecifiek</label>

                            {(stats.trainers.length <= 1 || (!stats.trainers.includes(props.trainer.uuid) && !stats.trainers.includes(""))) && !trainerSpecificFilter ? (
                                <>
                                    <div className={"radio"}>
                                        <input
                                            type={"radio"}
                                            id={"trainer_specific-none"}
                                            name={"trainer_specific"}
                                            value={""}
                                            onChange={(e) => {
                                                setTrainerSpecificFilter(null);
                                            }}
                                            checked={!trainerSpecificFilter}
                                            className={"radio__input"}
                                            disabled={true}
                                        />
                                        <label htmlFor={"trainer_specific-none"} className={"radio__label radio__label--disabled"}>
                                            Geen voorkeur
                                        </label>
                                    </div>
                                    <div className={"radio"}>
                                        <input
                                            type={"radio"}
                                            id={"trainer_specific-true"}
                                            name={"trainer_specific"}
                                            value={"true"}
                                            onChange={(e) => {
                                                setTrainerSpecificFilter(trainerSpecificOptions[0]);
                                            }}
                                            checked={stats.trainers.includes(props?.trainer?.uuid)}
                                            className={"radio__input"}
                                            disabled={true}
                                        />
                                        <label htmlFor={"trainer_specific-true"} className={"radio__label radio__label--disabled"}>
                                            Opleiderspecifiek
                                        </label>
                                    </div>
                                    <span className={"radio__disabled-message"}>Keuze niet mogelijk op basis van eerdere selecties</span>
                                </>
                            ) : (
                                <>
                                    <div className={"radio"}>
                                        <input
                                            type={"radio"}
                                            id={"trainer_specific-none"}
                                            name={"trainer_specific"}
                                            value={""}
                                            onChange={(e) => {
                                                setTrainerSpecificFilter(null);
                                            }}
                                            checked={!trainerSpecificFilter}
                                            className={"radio__input"}
                                        />
                                        <label htmlFor={"trainer_specific-none"} className={"radio__label"}>
                                            Geen voorkeur
                                        </label>
                                    </div>
                                    <div className={"radio"}>
                                        <input
                                            type={"radio"}
                                            id={"trainer_specific-true"}
                                            name={"trainer_specific"}
                                            value={"true"}
                                            onChange={(e) => {
                                                setTrainerSpecificFilter(trainerSpecificOptions[0]);
                                            }}
                                            checked={trainerSpecificFilter?.value === true}
                                            className={"radio__input"}
                                        />
                                        <label htmlFor={"trainer_specific-true"} className={"radio__label"}>
                                            Opleiderspecifiek
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/*<div className="col-4">*/}
                    {/*    <label className="form-input__label">Opleiderspecifiek</label>*/}
                    {/*    {(stats.trainers.length <= 1 || (!stats.trainers.includes(props.trainer.uuid) && !stats.trainers.includes(""))) && !trainerSpecificFilter ? (*/}
                    {/*        <input className={"form-control form-control-md form-control--disabled"} disabled type={"text"} value={ stats.trainers.includes(props.trainer.uuid) ? "Opleiderspecifiek" : !stats.trainers.includes(props.trainer.uuid) ? "Niet opleider specifiek" : "Geen optie beschikbaar"}/>*/}
                    {/*    ) : (*/}
                    {/*        <Select*/}
                    {/*            name="trainer_specific"*/}
                    {/*            value={trainerSpecificFilter}*/}
                    {/*            className="react-select-new"*/}
                    {/*            classNamePrefix="react-select"*/}
                    {/*            onChange={trainerSpecificOption => {setTrainerSpecificFilter(trainerSpecificOption); }}*/}
                    {/*            controlShouldRenderValue={true}*/}
                    {/*            options={trainerSpecificOptions}*/}
                    {/*            getOptionLabel={trainerSpecificOption => trainerSpecificOption.display_name}*/}
                    {/*            getOptionValue={trainerSpecificOption => trainerSpecificOption.value}*/}
                    {/*            isClearable={true}*/}
                    {/*            isSearchable={false}*/}
                    {/*            backspaceRemovesValue={true}*/}
                    {/*            placeholder={elearningOptions?.length > 0 ? "Geen voorkeur" : "Opties worden geladen"}*/}
                    {/*            noOptionsMessage={() => "Geen resultaten"}*/}
                    {/*        />*/}
                    {/*    )}*/}
                    {/*</div>*/}
                </div>

                <div className="form-section__row row">
                    <div className="form-input col-12">
                        <label className="form-input__label">Vrij zoeken</label>
                        <input
                            className="form-control form-control-md"
                            type="text"
                            name=""
                            placeholder={"Type cursus, titel (voor opleider), beschikbaarheid ..."}
                            value={cpNameFilter}
                            onChange={(e) => setCpNameFilter(e.target.value)}
                        />
                    </div>
                </div>

                <div className="form-section__row row">
                    <div className="col-12">
                        <label className="form-input__label">
                            Type cursus ({competenceProfiles?.length} {competenceProfiles?.length !== 1 ? "resultaten" : "resultaat"})
                        </label>

                        <table className={"competence-profile-selector"}>
                            <tbody>
                                {/*{competenceProfileUUID === "" || competenceProfileUUID === false && competenceProfiles?.length > 0 &&  (*/}
                                {/*    <tr key={"select-profile"} className={cx("competence-profile-selector__item", "competence-profile-selector__item--selected")}>*/}
                                {/*        <td className={"competence-profile-selector__column"}>*/}
                                {/*            {"Kies een competentieprofiel"}*/}
                                {/*        </td>*/}
                                {/*        <td className={"competence-profile-selector__column"}/>*/}
                                {/*    </tr>*/}
                                {/*)}*/}
                                {competenceProfiles?.length < 1 && (
                                    <tr key={"no-available-profile"} className={cx("competence-profile-selector__item")}>
                                        <td className={"competence-profile-selector__column"}>{"Geen competentieprofiel beschikbaar"}</td>
                                        <td className={"competence-profile-selector__column"} />
                                    </tr>
                                )}
                                {competenceProfiles?.map((profile) => {
                                    let profile_name = `${profile.course_name ? profile.course_name : profile.name}`;

                                    const disabled = profile.max_date && now.isAfter(profile.max_date);
                                    // Don't show unavailable profiles:
                                    if (disabled && profile.uuid !== competenceProfileUUID) {
                                        return null;
                                    }

                                    return (
                                        <tr
                                            key={profile.uuid}
                                            className={cx("competence-profile-selector__item", {
                                                "competence-profile-selector__item--selected": competenceProfileUUID === profile.uuid,
                                                "competence-profile-selector__item--disabled": disabled
                                            })}
                                            onClick={(e) => {
                                                if (disabled) {
                                                    return;
                                                }
                                                if (competenceProfileUUID !== profile.uuid && (scheduledEvents || courseEvents)) {
                                                    setConfirmSwitch(profile.uuid);
                                                } else {
                                                    setCompetenceProfileUUID(profile.uuid);
                                                }
                                            }}
                                        >
                                            <td className={"competence-profile-selector__column"}>{profile_name}</td>
                                            {/*<td className={"competence-profile-selector__column competence-profile-selector__code"}>*/}
                                            {/*    {profile?.code ? profile.code : ""}*/}
                                            {/*</td>*/}
                                            <td className={"competence-profile-selector__column competence-profile-selector__note"}>
                                                {minMaxDateText(profile.min_date, profile.max_date, profile.contains_elearning)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
});
